import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CurrencyInput } from "@/components/ui/currency-input";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/format";

function NoteField({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label className="block space-y-1.5 text-sm">
      <span className="text-muted-foreground">Napomena (nije obavezna)</span>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} maxLength={500} rows={3} placeholder={placeholder} />
    </label>
  );
}

export interface ResolveObligationDialogProps {
  clientName: string | null;
  amountKm: number;
  onClose: () => void;
  onConfirm: (note: string) => void;
  isPending: boolean;
}

export function ResolveObligationDialog({ clientName, amountKm, onClose, onConfirm, isPending }: ResolveObligationDialogProps) {
  const [note, setNote] = useState("");
  return (
    <Dialog open onClose={onClose} className="sm:max-w-md">
      <DialogHeader onClose={onClose}>
        <DialogTitle>Označi vraćeno</DialogTitle>
        <DialogDescription className="mt-0 text-xs">
          Potvrdite da je {formatCurrency(amountKm)}
          {clientName ? ` vraćeno klijentu ${clientName}` : " vraćeno"} kroz Monri portal ili bankovni nalog.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <NoteField value={note} onChange={setNote} placeholder="Npr. povrat u Monri portalu, referenca 123456" />
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isPending}>Nazad</Button>
        <Button onClick={() => onConfirm(note.trim())} disabled={isPending}>Označi vraćeno</Button>
      </DialogFooter>
    </Dialog>
  );
}

export interface StaleResolveDialogProps {
  mode: "not_charged" | "charged";
  amountKm: number;
  /** Pretplata: "nije naplaćeno" zatvara samo istekao kartični link, važeći ostaje važeći. */
  subscription?: boolean;
  onClose: () => void;
  onConfirm: (input: { note: string; amountKm: number | null }) => void;
  isPending: boolean;
}

export function StaleResolveDialog({ mode, amountKm, subscription, onClose, onConfirm, isPending }: StaleResolveDialogProps) {
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState<number | null>(amountKm);
  const charged = mode === "charged";
  return (
    <Dialog open onClose={onClose} className="sm:max-w-md">
      <DialogHeader onClose={onClose}>
        <DialogTitle>{charged ? "Naplaćeno u Monriju" : "Nije naplaćeno"}</DialogTitle>
        <DialogDescription className="mt-0 text-xs">
          {charged
            ? "Uplata se zatvara kao vraćena i otvara se povrat klijentu na listi iznad."
            : subscription
              ? "Pretplata se skida sa ove liste. Istekao link se zatvara kao neuspio, važeći link ostaje važeći. Uradite ovo samo kad u Monri portalu nema naplate."
              : "Uplata se zatvara kao neuspjela. Uradite ovo samo kad u Monri portalu nema naplate."}
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        {charged && (
          <label className="block space-y-1.5 text-sm">
            <span className="text-muted-foreground">Naplaćen iznos</span>
            <CurrencyInput value={amount} onChange={setAmount} suffix="KM" />
          </label>
        )}
        <NoteField
          value={note}
          onChange={setNote}
          placeholder={charged ? "Npr. naplata vidljiva u portalu 23.10." : "Npr. narudžba u portalu bez naplate"}
        />
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isPending}>Nazad</Button>
        <Button
          onClick={() => onConfirm({ note: note.trim(), amountKm: charged ? amount : null })}
          disabled={isPending || (charged && !(amount && amount > 0))}
        >
          {charged ? "Otvori povrat" : "Zatvori uplatu"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
